
import axios from 'axios';

import {
  defineComponent,
  defineAsyncComponent,
  ref,
  reactive,
  computed,
  onMounted,
} from 'vue';

import { useRouter } from 'vue-router';

import PrimeVueToast from 'primevue/toast';

import PrimeVueConfirmPopup from 'primevue/confirmpopup';

import {
  GenericObject,
  CreativeImageFormatOption,
  ErrorsMap,
  Projects,
  Creative,
} from '@/types';

import {
  ENTRIES,
  CREATIVE_TYPE_PUSH,
  CREATIVE_TYPE_NATIVE,
  CREATIVE_TYPE_BANNER,
  CREATIVE_TYPES,
  CREATIVE_IMAGE_FORMAT_W_300_H_250,
  CREATIVE_IMAGE_FORMATS,
  CREATIVE_IMAGE_FORMATS_MAP,
  CREATIVE_APPROVAL_STATUS_PENDING_APPROVAL,
  CREATIVE_APPROVAL_STATUS_DECLINED,
  CREATIVE_APPROVAL_STATUSES,
} from '@/libs/consts';

import useUser from '@/composable/useUser';

import useEntries from '@/composable/useEntries';

import useEntriesNext from '@/composable/useEntries@next';

import useCreativeActions from '@/composable/useCreativeActions';

export default defineComponent({
  components: {
    PrimeVueToast,
    PrimeVueConfirmPopup,
    Macros: defineAsyncComponent(() => import('@/components/Macros.vue')),
    FieldImageBase64Cropper: defineAsyncComponent(() => import('@/components/FieldImageBase64Cropper.vue')),
    ApprovalStatusComment: defineAsyncComponent(() => import('@/components/ApprovalStatusComment.vue')),
  },
  setup() {
    const router = useRouter();

    const { activeUser: user } = useUser();

    const {
      cloningCreativeId,
      confirmCloneCreative,
      deletingCreativeId,
      confirmDeleteCreative,
      restoringCreativeId,
      restoreCreative,
    } = useCreativeActions();

    const {
      entries: projects,
      entriesLoading: projectsLoading,
      fetchEntries: fetchProjects,
    } = useEntriesNext<Projects>('/api/project/getProjects', ENTRIES);

    const { getEntryByRoute } = useEntries();

    const creative = reactive<Creative>({
      type: CREATIVE_TYPE_PUSH,
      approval_status: CREATIVE_APPROVAL_STATUS_PENDING_APPROVAL,
      approval_status_comment: null,
    });

    const creativeMedia = reactive<GenericObject>({
      icon_base64: null,
      image_base64: null,
    });

    const creativeErrorsMap = ref<ErrorsMap>({});

    const imageStencilProps = computed(() => {
      if (creative.type === CREATIVE_TYPE_PUSH) {
        return {
          aspectRatio: 3 / 2,
        };
      }

      if (!creative.image_format) {
        return undefined;
      }

      return (CREATIVE_IMAGE_FORMATS_MAP[creative.image_format] as CreativeImageFormatOption).stencil_props;
    });

    const imageStencilSize = computed(() => {
      if (creative.type === CREATIVE_TYPE_PUSH) {
        return undefined;
      }

      if (!creative.image_format) {
        return undefined;
      }

      return (CREATIVE_IMAGE_FORMATS_MAP[creative.image_format] as CreativeImageFormatOption).stencil_size;
    });

    const fetchCreative = async (): Promise<void> => {
      const entry = await getEntryByRoute('/api/creative/getCreativeById');

      if (entry) {
        Object.assign(creative, entry);
      }

      Promise.all([
        user.isSuperAdmin() ? fetchProjects() : Promise.resolve(),
      ]);
    };

    const storeCreative = async (): Promise<void> => {
      const response = await axios.post(
        '/api/creative/storeCreative',
        {
          ...creative,
          ...creativeMedia,
        },
      );

      Object.assign(creative, response.data);
    };

    const onCloneSuccess = async (newId: number): Promise<void> => {
      await router.push(`/creatives/${newId}`);
    };

    const onDeleteSuccess = async (): Promise<void> => {
      await router.push('/creatives');
    };

    const onRestoreSuccess = (): void => {
      creative.deleted_at = null;
    };

    const onProjectChange = (): void => {
      creative.user_id = undefined;
    };

    const onTypeChange = (): void => {
      creative.image_format = undefined;

      creativeMedia.icon_base64 = null;

      creativeMedia.image_base64 = null;

      // Для Native пока ограничиваемся форматом 300x250
      if (creative.type === CREATIVE_TYPE_NATIVE) {
        creative.image_format = CREATIVE_IMAGE_FORMAT_W_300_H_250;
      }
    };

    const onFormatChange = (): void => {
      creativeMedia.image_base64 = null;
    };

    onMounted((): void => {
      Promise.all([
        user.isSuperAdmin() ? fetchProjects() : Promise.resolve(),
      ]);
    });

    return {
      CREATIVE_TYPE_PUSH,
      CREATIVE_TYPE_NATIVE,
      CREATIVE_TYPE_BANNER,
      CREATIVE_TYPES,
      CREATIVE_IMAGE_FORMATS,
      imageStencilProps,
      imageStencilSize,
      CREATIVE_APPROVAL_STATUS_DECLINED,
      CREATIVE_APPROVAL_STATUSES,
      user,
      projectsLoading,
      projects,
      creative,
      creativeMedia,
      creativeErrorsMap,
      fetchCreative,
      storeCreative,
      cloningCreativeId,
      confirmCloneCreative,
      deletingCreativeId,
      confirmDeleteCreative,
      restoringCreativeId,
      restoreCreative,
      onCloneSuccess,
      onDeleteSuccess,
      onRestoreSuccess,
      onProjectChange,
      onTypeChange,
      onFormatChange,
    };
  },
});
